import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, fmtMoney } from "@/lib/api";
import { PageHeader, Panel, LoadingState } from "@/components/common";
import { DemoBadge } from "@/components/DemoBadge";
import { Calculator, CheckCircle2 } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

export default function Accounting() {
  const [fy, setFy] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["accounting", fy],
    queryFn: async () => (await api.get("/accounting/accounts", { params: fy != null ? { fy } : {} })).data,
  });
  const { data: bs } = useQuery({
    queryKey: ["balance-sheet"],
    queryFn: async () => (await api.get("/finance/balance-sheet")).data,
  });

  if (isLoading) return <LoadingState />;
  if (fy == null && data?.fy_start != null) setFy(data.fy_start);

  const typeColor = { Asset: "text-blue-400", Equity: "text-purple-400", Income: "text-emerald-400", Expense: "text-rose-400", Liability: "text-amber-400" };
  const tb = data.trial_balance;

  return (
    <div>
      <PageHeader
        title="Accounting & Finance"
        subtitle={`Double-entry ledger · Indian FY (Apr 1 – Mar 31)`}
        icon={Calculator}
        testId="accounting-header"
        actions={
          <Select value={String(data.fy_start)} onValueChange={(v) => setFy(Number(v))}>
            <SelectTrigger data-testid="fy-selector" className="h-9 w-[180px] text-xs bg-[var(--bg-canvas)] border-[var(--border-subtle)] text-slate-200">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[var(--card)] border-[var(--border-active)] text-slate-200">
              {data.available_financial_years.map((y) => (
                <SelectItem key={y.start} value={String(y.start)}>{y.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      <div className="flex items-center gap-2 mb-4 rounded-lg border border-[var(--border-subtle)] bg-[var(--card)] px-4 py-2.5">
        <span className="text-xs text-slate-400">Viewing</span>
        <span className="text-xs font-semibold text-slate-100" data-testid="current-fy">{data.financial_year}</span>
        <span className="ml-auto flex items-center gap-1.5 text-xs">
          Trial Balance:
          {tb.balanced ? (
            <span className="text-emerald-400 flex items-center gap-1" data-testid="tb-status"><CheckCircle2 className="w-3.5 h-3.5" /> Balanced ({fmtMoney(tb.total_debit)})</span>
          ) : (
            <span className="text-rose-400" data-testid="tb-status">Unbalanced</span>
          )}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="Chart of Accounts (Opening → Closing)" testId="accounts-panel" actions={<DemoBadge />}>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="accounts-table">
              <thead className="bg-[var(--surface)]">
                <tr className="border-b border-[var(--border-subtle)] text-slate-400">
                  <th className="px-3 py-2 text-left font-medium">Code</th>
                  <th className="px-3 py-2 text-left font-medium">Account</th>
                  <th className="px-3 py-2 text-left font-medium">Type</th>
                  <th className="px-3 py-2 text-right font-medium">Opening</th>
                  <th className="px-3 py-2 text-right font-medium">Closing</th>
                </tr>
              </thead>
              <tbody>
                {data.accounts.map((a) => (
                  <tr key={a.id} className="border-b border-[var(--border-subtle)] hover:bg-[var(--card-hover)]">
                    <td className="px-3 py-2.5 font-mono-num text-slate-500">{a.code}</td>
                    <td className="px-3 py-2.5 text-slate-200">{a.name}</td>
                    <td className={cn("px-3 py-2.5 font-medium", typeColor[a.type])}>{a.type}</td>
                    <td className="px-3 py-2.5 text-right font-mono-num text-slate-500">{fmtMoney(a.opening_balance)}</td>
                    <td className="px-3 py-2.5 text-right font-mono-num text-slate-200">{fmtMoney(a.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel title="Journal Entries (Double-Entry)" testId="ledger-panel" actions={<DemoBadge />}>
          <div className="divide-y divide-[var(--border-subtle)] max-h-[420px] overflow-y-auto">
            {data.entries.length === 0 ? (
              <div className="px-4 py-10 text-center text-xs text-slate-600">No journal entries in this financial year.</div>
            ) : data.entries.map((e) => (
              <div key={e.id} className="px-4 py-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-slate-200">{e.narration}</span>
                  <span className="flex items-center gap-1 text-[10px] text-emerald-400"><CheckCircle2 className="w-3 h-3" /> Balanced</span>
                </div>
                <div className="text-[11px] text-slate-500 mb-1.5">{new Date(e.date).toLocaleDateString("en-IN")}</div>
                <div className="space-y-0.5">
                  {e.lines.map((ln, i) => (
                    <div key={i} className="flex items-center justify-between font-mono-num text-[11px]">
                      <span className="text-slate-400">{ln.account_code}</span>
                      <span className="text-emerald-400 w-28 text-right">{Number(ln.debit) > 0 ? fmtMoney(ln.debit) : ""}</span>
                      <span className="text-rose-400 w-28 text-right">{Number(ln.credit) > 0 ? fmtMoney(ln.credit) : ""}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      {bs && (
        <Panel title="Balance Sheet (Assets = Liabilities + Equity)" testId="balance-sheet-panel" className="mt-4" actions={<DemoBadge />}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 divide-y lg:divide-y-0 lg:divide-x divide-[var(--border-subtle)]">
            <div className="p-4">
              <div className="text-[11px] uppercase tracking-wide text-blue-400 font-semibold mb-2">Assets (at cost)</div>
              {bs.assets.map((a) => (
                <div key={a.code} className="flex justify-between text-xs py-1">
                  <span className="text-slate-400">{a.code} · {a.name}</span>
                  <span className="font-mono-num text-slate-200">{fmtMoney(a.amount)}</span>
                </div>
              ))}
              <div className="flex justify-between text-xs py-1.5 mt-1 border-t border-[var(--border-subtle)] font-semibold">
                <span className="text-slate-300">Total Assets</span>
                <span className="font-mono-num text-blue-400">{fmtMoney(bs.totals.total_assets_cost)}</span>
              </div>
            </div>
            <div className="p-4">
              <div className="text-[11px] uppercase tracking-wide text-amber-400 font-semibold mb-2">Liabilities</div>
              {bs.liabilities.length === 0 ? <div className="text-xs text-slate-600 py-1">None</div> : bs.liabilities.map((l) => (
                <div key={l.code} className="flex justify-between text-xs py-1"><span className="text-slate-400">{l.name}</span><span className="font-mono-num text-slate-200">{fmtMoney(l.amount)}</span></div>
              ))}
              <div className="flex justify-between text-xs py-1.5 mt-1 border-t border-[var(--border-subtle)] font-semibold">
                <span className="text-slate-300">Total Liabilities</span>
                <span className="font-mono-num text-amber-400">{fmtMoney(bs.totals.total_liabilities)}</span>
              </div>
            </div>
            <div className="p-4">
              <div className="text-[11px] uppercase tracking-wide text-purple-400 font-semibold mb-2">Equity (at cost)</div>
              {bs.equity.map((e) => (
                <div key={e.code} className="flex justify-between text-xs py-1"><span className="text-slate-400">{e.code} · {e.name}</span><span className="font-mono-num text-slate-200">{fmtMoney(e.amount)}</span></div>
              ))}
              <div className="flex justify-between text-xs py-1.5 mt-1 border-t border-[var(--border-subtle)] font-semibold">
                <span className="text-slate-300">Total Equity</span>
                <span className="font-mono-num text-purple-400">{fmtMoney(bs.totals.total_equity_cost)}</span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4 px-4 py-3 border-t border-[var(--border-subtle)] text-xs">
            <span className="text-slate-500">Unrealized valuation adj. (memo): <span className="font-mono-num text-emerald-400">{fmtMoney(bs.totals.unrealized_valuation_adjustment)}</span></span>
            <span className="text-slate-500">Equity at market: <span className="font-mono-num text-slate-200">{fmtMoney(bs.totals.total_equity_market)}</span></span>
            <span className="ml-auto flex items-center gap-1.5" data-testid="bs-balanced">
              {bs.reconciliation.balanced
                ? <span className="text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Balanced ({fmtMoney(bs.reconciliation.assets_cost)})</span>
                : <span className="text-rose-400">Unbalanced</span>}
            </span>
          </div>
        </Panel>
      )}
    </div>
  );
}
